import { Component, ElementRef, HostBinding, HostListener, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';
import { FormGroup } from '@angular/forms';
import { CoreSidebarService } from '@core/components/core-sidebar/core-sidebar.service';
import { CoreConfigService } from '@core/services/config.service';
import { CoreMediaService } from '@core/services/media.service';
import { TranslocoService } from '@jsverse/transloco';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Actions, ofType } from '@ngrx/effects';
import { User } from 'app/auth/models';
import { AuthenticationService } from 'app/auth/service';
import { GlobalService } from 'app/global/global.service';
import { PermissionsEnum } from 'app/global/permissions/permissions.enum';
import { PermissionsService } from 'app/global/permissions/permissions.service';
import { UploadsService } from 'app/global/uploads.service';
import { AddNewComponent } from 'app/main/pages/general-modals/add-new/add-new.component';
import { SelectTemplateComponent } from 'app/main/pages/general-modals/select-template/select-template.component';
import { UserSettingsFacade } from 'app/store/user-settings.facade';
import * as _ from 'lodash';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { delay, take, takeUntil } from 'rxjs/operators';
import { CoreConfig } from '../../../../@core/types';
import { environment } from '../../../../environments/environment';
import { TranslationsHelperService } from '../../../../translations.helper.service';
import { IIDNAmeConstant } from '../../../constants/constants';
import { UserSettings, Workspace } from '../../../global/user-settings.dto';
import { UserSettingsActionTypes } from '../../../store/actions/user.actions';
import { WorkspaceFormModalComponent } from '../workspace-form-modal/workspace-form-modal.component';
import { WorkspaceService } from './workspace.service';
import { SessionImageService } from 'app/global/session-image.service';
import { DocumentFileUploadModalComponent } from 'app/main/pages/documents/modals/document-file-upload-modal/document-file-upload-modal.component';
import { GenerateFromDocumentOrPromptModalComponent } from 'app/main/pages/general-modals/generate-from-document-or-prompt/generate-from-document-or-prompt-modal.component';
import { GenerateDocumentModalService } from 'app/main/pages/documents/generate-document-modal.service';
import { AddToContractControlModalComponent, SectionEnum } from 'app/main/pages/documents/document-right-sidebar/add-to-contract-control-modal/add-to-contract-control-modal.component';
import { AuthFacade } from 'app/store/auth.facade';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class NavbarComponent implements OnInit, OnDestroy {
  @ViewChild('uploadFileInput') uploadFileInput: ElementRef;

  canViewRolesTab$ = this._permissionService.canViewRoles();
  canViewUsersTab$ = this._permissionService.canViewUsers();
  canViewGroupsTab$ = this._permissionService.canViewGroups();

  public userSettings$ = this._userSettingsFacade$._userSettings$;
  public horizontalMenu: boolean;
  public hiddenMenu: boolean;
  public profileData: UserSettings;
  public canUploadDocument$ = this._permissionService.isNotRestricted(PermissionsEnum.restrict_access_send_documents_not_templates);
  public canUploadDocumentsForArchive$ = this._permissionService.isNotRestricted(PermissionsEnum.restrict_access_upload_documents);
  public coreConfig: CoreConfig;
  public currentSkin: string;
  public prevSkin: string;
  public object = Object.keys;
  public acceptableFiles: string[] = ["application/pdf", "application/msword"];
  public currentUser: User;
  public languageOptions: unknown;
  public navigation: string;
  public selectedLanguage: string;
  public receivedMessage: string;
  public eventSubscription: Subscription;
  public coreConfigSubscription: Subscription;
  public activeWorkspace$: Observable<Workspace> = this.globalService.getUserDefaultWorkSpace();
  public workspaceForm: FormGroup;
  public workspaceSubmitted: boolean = false;
  public workspaceLoading: boolean = false;
  public selectedWorkspaceIndex: number = -1;
  public remainingDays: number = 0;
  public readonly languageKeyDictionary: { [key: string]: string } = {
    'en': 'English',
    'sv': 'Swedish',
    'de': 'German',
    'fi': 'Finish',
    'fr': 'French',
    'no': 'Norwegian',
    'da': 'Danish',
    'es': 'Spanish',
    'pl': 'Polish',
    'sl': 'Slovak'
  }

  @HostBinding('class.fixed-top')
  public isFixed = false;

  @HostBinding('class.navbar-static-style-on-scroll')
  public windowScrolled = false;

  // Add .navbar-static-style-on-scroll on scroll using HostListener & HostBinding
  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (
      (window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop > 100) &&
      this.coreConfig.layout.navbar.type == 'navbar-static-top' &&
      this.coreConfig.layout.type == 'horizontal'
    ) {
      this.windowScrolled = true;
    } else if (
      (this.windowScrolled && window.pageYOffset) ||
      document.documentElement.scrollTop ||
      document.body.scrollTop < 10
    ) {
      this.windowScrolled = false;
    }
  }

  private userAvatarResolverTimeout;
  private allowedTrailDays: number = 14;

  // Private
  private _unsubscribeAll: Subject<void> = new Subject<void>();

  /**
   * Constructor
   *
   * @param {AuthenticationService} _authenticationService
   * @param {CoreConfigService} _coreConfigService
   * @param {CoreSidebarService} _coreSidebarService
   * @param {CoreMediaService} _coreMediaService
   * @param {MediaObserver} _mediaObserver
   * @param modalService
   * @param workspaceService
   * @param globalService
   * @param transloco
   * @param _uploadingService$
   * @param _translationsHelperService
   * @param _permissionService
   * @param _userSettingsFacade$
   * @param _actions
   * @param _cdr
   */
  constructor(
    private _authenticationService: AuthenticationService,
    private _coreConfigService: CoreConfigService,
    private _coreMediaService: CoreMediaService,
    private _coreSidebarService: CoreSidebarService,
    private _mediaObserver: MediaObserver,
    private modalService: NgbModal,
    private workspaceService: WorkspaceService,
    public globalService: GlobalService,
    public transloco: TranslocoService,
    private _uploadingService$: UploadsService,
    private _translationsHelperService: TranslationsHelperService,
    private _permissionService: PermissionsService,
    private _userSettingsFacade$: UserSettingsFacade,
    private _authFacade: AuthFacade,
    private _actions: Actions,
    public sessionImageService: SessionImageService,
    private generateTemplateModalService: GenerateDocumentModalService,
  ) { }

  public userAvatarRefresher$: Observable<boolean> = this.avatarRefresher()
  public selectedWorkspace: number = 1;
  public workspace: Array<IIDNAmeConstant> = [
    { id: 1, name: 'English' },
    { id: 2, name: 'Swedish' },
    { id: 3, name: 'German' },
  ];

  // Public Methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle sidebar open
   *
   * @param key
   */
  toggleSidebar(key: string): void {
    this._coreSidebarService.getSidebarRegistry(key).toggleOpen();
  }

  /**
   * Set the language
   *
   * @param language
   */
  setLanguage(language): void {
    // Set the selected language for the navbar on change
    this.selectedLanguage = language;
    // Use the selected language id for translations
    language = this._translationsHelperService.changeLanguage(language);
    this._coreConfigService.setConfig({ app: { appLanguage: language } }, { emitEvent: true });
    setTimeout(() => location.reload());
  }

  /**
    * Open bulk upload modal
    */
  documentUploadFileModalClick() {
    this.canUploadDocumentsForArchive$.pipe(take(1)).subscribe(canUpload => {
      if (!canUpload) {
        return;
      }
      const modalRef = this.modalService.open(DocumentFileUploadModalComponent, {
        size: 'xl',
      });
      modalRef.componentInstance.files = [];
    });
  }

  /**
   * Opens a modal for generating a document using AI-based document generation.
   * The modal allows the user to choose options for document generation and provides
   * a title for the modal window.
   *
   * The modal component used is `GenerateFromDocumentOrPromptModalComponent`, and
   * it is configured with the necessary services and options.
   */
  generateDocument() {
    const modal = this.modalService.open(GenerateFromDocumentOrPromptModalComponent);
    modal.componentInstance.generationService = this.generateTemplateModalService;
    modal.componentInstance.showOptions = true;
    modal.componentInstance.title = this.transloco.translate("Modal title (AI document generation modal) - New Document");
    modal.componentInstance.submitButtonTitle = this.transloco.translate("Submission button title (AI document generation modal) - Generate Template");

  }

  /**
   * Opens a modal dialog for adding a contract control.
   * 
   * This method uses the `modalService` to open the `AddToContractControlModalComponent` 
   * with specific configurations such as size, backdrop behavior, and custom window class.
   * It also sets the `section` property of the modal component instance to 
   * `SectionEnum.CONTRACT_CONTROL`.
   */
  addContractModalModalClick() {
    const modalRef$ = this.modalService.open(AddToContractControlModalComponent, {
      size: 'lg',
      backdrop: 'static',
      windowClass: 'add-to-contract-control-modal'
    });
    modalRef$.componentInstance.section = SectionEnum.CONTRACT_CONTROL;
  }


  onFileDropped($event: Event) {
    let files: File[] = [];
    if ($event instanceof Event) {
      files = [
        ...(($event.target as HTMLInputElement).files) as unknown as File[]
      ];
    } else {
      files = $event as unknown as File[];
    }
    this._uploadingService$.uploadFiles(files)
    if ($event instanceof Event) {
      this.uploadFileInput.nativeElement.value = null;
    }
  }

  /**
   * Toggle Dark Skin
   */
  toggleDarkSkin() {
    // Get the current skin
    this._coreConfigService
      .getConfig()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(config => {
        this.currentSkin = config.layout.skin;
      });

    // Toggle Dark skin with prevSkin skin
    this.prevSkin = localStorage.getItem('prevSkin');

    if (this.currentSkin === 'dark') {
      this._coreConfigService.setConfig(
        { layout: { skin: this.prevSkin ? this.prevSkin : 'default' } },
        { emitEvent: true }
      );
    } else {
      localStorage.setItem('prevSkin', this.currentSkin);
      this._coreConfigService.setConfig({ layout: { skin: 'dark' } }, { emitEvent: true });
    }
  }


  initialName(fullName: string) {
    const allNames = fullName.trim().split(' ');
    const initials = allNames.reduce((acc, curr, index) => {
      if (index === 0 || index === allNames.length - 1) {
        acc = `${acc}${curr.charAt(0).toUpperCase()}`;
      }
      return acc;
    }, '');
    return initials;
  }

  /**
   * Logout method
   */
  async logout() {
    this._authenticationService.logMeOut();
  }

  // Lifecycle Hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // get the currentUser details from localStorage
    this.currentUser = JSON.parse(localStorage.getItem('currentUser'));
    // Subscribe to userSettings$ to access remaining_days
    this.userSettings$.pipe(takeUntil(this._unsubscribeAll)).subscribe((userSettings) => {
      this.remainingDays = userSettings?.remaining_days ?? 0;
    });
    // Subscribe to the config changes(userSettings$ | async)?
    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(config => {
      this.coreConfig = config;
      this.horizontalMenu = config.layout.type === 'horizontal';
      this.hiddenMenu = config.layout.menu.hidden === true;
      this.currentSkin = config.layout.skin;

      // Fix: for vertical layout if default navbar fixed-top than set isFixed = true
      if (this.coreConfig.layout.type === 'vertical') {
        this.coreConfigSubscription = of(null).
          pipe(
            delay(0),
            take(1)
          ).subscribe(() => {
            if (this.coreConfig.layout.navbar.type === 'fixed-top') {
              this.isFixed = true;
            }
          });
      }
    });

    // Horizontal Layout Only: Add class fixed-top to navbar below large screen
    if (this.coreConfig.layout.type == 'horizontal') {
      // On every media(screen) change
      this._coreMediaService.onMediaUpdate.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
        const isFixedTop = this._mediaObserver.isActive('bs-gt-xl');
        if (isFixedTop) {
          this.isFixed = false;
        } else {
          this.isFixed = true;
        }
      });
    }

    // Set the selected language from default languageOptions
    this.selectedLanguage = _.find(this.languageOptions, {
      id: this.transloco.getActiveLang()
    });
  }



  /**
   * Switch workspace
   * @param workspaceId 
   */
  switchWorkspace(workspaceId: number) {
    this.workspaceService.switchWorkspace(workspaceId).subscribe({
      next: () => window.location.reload(),
      error: (error: string) => this.globalService.toastrError(error)
    });
  }

  /**
   * open modal to edit workspace 
   * @param event 
   * @param item 
   * @param workspaceModal 
   * @param workspaceIndex 
   */
  editWorkspace(event: Event, item: Workspace) {
    event.stopPropagation();
    event.preventDefault();
    this.openWorkSpaceModal(item);
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
    if (this.coreConfigSubscription) {
      this.coreConfigSubscription.unsubscribe();
    }

    clearTimeout(this.userAvatarResolverTimeout);
  }

  /**
   * open the select template modal where user can select the template for send document
   */
  openSelectTemplate() {
    this.modalService.open(SelectTemplateComponent, {
      size: 'lg',
      windowClass: 'select-template-modal',
    });
  }

  /**
   * create a new blank document
   */
  createNewDocument() {
    const addNew = this.modalService.open(AddNewComponent);
    addNew.componentInstance.title = this.transloco.translate("Add New Document (general) - Add New Document");
    addNew.componentInstance.label = this.transloco.translate("Document name (general) - Document name");
  }




  modalOpenBillingModal(billingModal) {
    this.modalService.open(billingModal, {
      size: 'xl',
      windowClass: 'billing-modal',
    });
  }



  /**
   * Open workspace modal
   * @param workspace 
   */
  openWorkSpaceModal(workspace: Workspace) {
    const modalRef$ = this.modalService.open(WorkspaceFormModalComponent);
    modalRef$.componentInstance.workspace = workspace;
    modalRef$.result.then((result: Workspace | { deleted: boolean; id: number }) => {
      if (result !== undefined && (result?.id || 'deleted' in result)) {
        this._authFacade.fetchUserSettings();
      }
    });
  }



  getAvailableLangs() {
    return this.transloco?.getAvailableLangs()?.filter(x => (x as string)?.toLowerCase() != environment.locale?.fallbackLang);
  }

  private avatarRefresher(): Observable<boolean> {
    return new Observable<boolean>((resolver) => {
      resolver.next(true); // display avatar by default
      this._actions
        .pipe(
          ofType(UserSettingsActionTypes.REFRESH_USER_AVATAR),
        )
        .subscribe(() => {
          // if avatar has been changed in a background, we have to recreate avatar component to reflect changes,
          resolver.next(false);
          if (this.userAvatarResolverTimeout) {
            clearTimeout(this.userAvatarResolverTimeout);
          }
          this.userAvatarResolverTimeout = setTimeout(() => { //creating a small delay to make Angular a component recreation by ngIf
            resolver.next(true);
          }, 100)

        })
    })
  }
}